import React, { useEffect, useState } from "react";
import axios from "axios";
import AdminHeader from "../Layout/AdminHeader"; 
import AdminSideBar from "../Admin/Layout/AdminSideBar";
import { DataGrid } from "@mui/x-data-grid";
import { Button, CircularProgress } from "@mui/material";
import { server } from "../../server";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const AdminViewSellerInvoices = () => {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSellerInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchSellerInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${server}/sellerInvoice/get-all-seller-invoices`,
        {
          params: startDate && endDate ? { startDate, endDate } : {},
          withCredentials: true,
        }
      );
      if (response.data.success) {
        const sortedInvoices = response.data.sellerInvoices.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setRows(
          sortedInvoices.map((invoice) => ({
            id: invoice._id,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: new Date(invoice.date)
              .toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
              .replace(/\//g, "-"),
            sellerName: invoice.sellerId.name,
            totalValueWithoutGst: (invoice.totalValueWithoutGst || 0).toFixed(2),
            totalGstValue: (invoice.totalGstValue || 0).toFixed(2),
            shippingCharges: (invoice.shippingCharges || 0).toFixed(2),
            totalInvoiceValue: (invoice.totalInvoiceValue || 0).toFixed(2),
            status: invoice.status,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch seller invoices", error);
      alert("Failed to fetch seller invoices. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = (invoiceId) => {
    navigate(`/admin-edit-seller-invoice/${invoiceId}`);
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this seller invoice? This will remove all associated transactions."
      )
    ) {
      try {
        const response = await axios.delete(
          `${server}/sellerInvoice/delete-seller-invoice/${invoiceId}`,
          { withCredentials: true }
        );
        if (response.data.success) {
          alert("Seller invoice deleted successfully");
          setRows((prevRows) => prevRows.filter((row) => row.id !== invoiceId));
        } else {
          alert("Failed to delete seller invoice");
        }
      } catch (error) {
        console.error("Failed to delete seller invoice", error);
        alert("An error occurred while deleting the seller invoice.");
      }
    }
  };

  const handleDownloadExcel = async () => {
    if (rows.length === 0) {
      alert("No data available to download.");
      return;
    }

    setLoading(true);
    try {
      const dataToExport = rows.map((row) => ({
        "Invoice Number": row.invoiceNumber,
        "Invoice Date": row.invoiceDate,
        "Seller Name": row.sellerName,
        "Total Value (Without GST)": parseFloat(row.totalValueWithoutGst),
        "Total GST Value": parseFloat(row.totalGstValue),
        "Shipping Charges": row.shippingCharges
          ? parseFloat(row.shippingCharges)
          : 0.0,
        "Total Invoice Value": parseFloat(row.totalInvoiceValue),
        Status: row.status,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Seller Invoices");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
      const fileName = `Seller_Invoices_${startDate || "Any"}_to_${endDate || "Any"}.xlsx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Excel file:", error);
      alert("An error occurred while generating the Excel file.");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "invoiceNumber", headerName: "Invoice Number", width: 150 },
    { field: "invoiceDate", headerName: "Invoice Date", width: 150 },
    { field: "sellerName", headerName: "Seller Name", width: 150 },
    {
      field: "totalValueWithoutGst",
      headerName: "Total Value (Without GST)",
      width: 200,
    },
    { field: "totalGstValue", headerName: "Total GST Value", width: 150 },
    { field: "shippingCharges", headerName: "Shipping Charges", width: 150 },
    {
      field: "totalInvoiceValue",
      headerName: "Total Invoice Value",
      width: 180,
    },
    {
      field: "editInvoice",
      headerName: "Edit Invoice",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleEditInvoice(params.row.id)}
          variant="contained"
          color="default"
          style={{ marginLeft: 8, fontSize: "0.8rem" }}
        >
          Edit
        </Button>
      ),
    },
    {
      field: "deleteInvoice",
      headerName: "Delete Invoice",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          onClick={() => handleDeleteInvoice(params.row.id)}
          variant="contained"
          color="secondary"
          style={{ fontSize: "0.8rem" }}
        >
          Delete
        </Button>
      ),
    },
    { field: "status", headerName: "Status", minWidth: 120, flex: 1 },
  ];

  return (
    <div>
      <AdminHeader />
      <div className="w-full flex">
        <div className="flex items-start justify-between w-full">
          <div className="w-[80px] 800px:w-[330px]">
            <AdminSideBar active={10} />
          </div>
          <div className="w-full min-h-[45vh] pt-5 rounded flex justify-center">
            <div className="w-[97%] flex justify-center flex-col">
              <div className="flex justify-between mb-4">
                <div className="flex items-center">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded mr-2"
                    placeholder="Start Date"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded"
                    placeholder="End Date"
                  />
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={handleDownloadExcel}
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Download Excel"
                    )}
                  </Button>
                </div>
              </div>

              <style>
                {`
                  @media print {
                    body * {
                      visibility: hidden;
                    }
                    .printable-area, .printable-area * {
                      visibility: visible;
                    }
                    .printable-area {
                      position: absolute;
                      left: 0;
                      top: 0;
                      width: 100%;
                    }
                  }
                `}
              </style>

              <div className="printable-area">
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <CircularProgress />
                  </div>
                ) : (
                  // Outer scrollable container
                  <div className="w-full overflow-x-auto scroll-smooth">
                    {/* Inner container forces a minimum width for horizontal scrolling */}
                    <div style={{ minWidth: "1550px" }}>
                      <DataGrid
                        rows={rows}
                        columns={columns}
                        pageSize={20}
                        disableSelectionOnClick
                        autoHeight
                        sx={{
                          fontSize: "0.8rem",
                          "& .MuiDataGrid-cell": { fontSize: "0.8rem", padding: "0.5rem" },
                          "& .MuiDataGrid-columnHeader": {
                            fontSize: "0.8rem",
                            padding: "0.5rem",
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminViewSellerInvoices;
